import React, { useState, useEffect } from 'react';
import { useTranslation } from '../context/TranslationContext';
import './Products.css';

const Products = () => {
  const [activeTab, setActiveTab] = useState('cattle-feed');
  const [selectedItems, setSelectedItems] = useState({
    'cattle-feed': {},
    'raw-materials': {}
  });
  const [orderForm, setOrderForm] = useState({
    name: '',
    phone: '',
    email: ''
  });
  const { t } = useTranslation();

  // Load selected items from localStorage on component mount
  useEffect(() => {
    const savedItems = localStorage.getItem('ramson-feeds-selected-items');
    if (savedItems) {
      setSelectedItems(JSON.parse(savedItems));
    }
  }, []);

  // Save selected items to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('ramson-feeds-selected-items', JSON.stringify(selectedItems));
  }, [selectedItems]);

  const cattleFeedProducts = [
    { id: 1, name: t('maan'), image: '/images/maan.png', height: 270 },
    { id: 3, name: t('goat'), image: '/images/goat.png', height: 270  },
    { id: 4, name: t('ramachandra'), image: '/images/ram.png', height: 270  },
    { id: 2, name: t('komatha'), image: '/images/komatha.png', height: 300 },
    { id: 5, name: t('ivory'), image: '/images/ivory.png', height: 370  },
    { id: 6, name: t('no2'), image: '/images/no2.png', height: 350  },
    { id: 7, name: t('spkomatha'), image: '/images/komathatoor.png', height: 350  },
    { id: 8, name: t('srtc'), image: '/images/srtc.png', height: 350  },
    { id: 9, name: t('smr'), image: '/images/smr.png', height: 300  },
    { id: 10, name: t('apple'), image: '/images/apple.png', height: 300  },
    { id: 11, name: t('mgs'), image: '/images/mgs.png', height: 300  },
    { id: 12, name: t('appu'), image: '/images/appu.png', height: 300  },
  ];

  const rawMaterials = [
    { id: 1, name: t('toor'), description: t('toord'), image: '/images/toor1.jpg' },
    { id: 2, name: t('orid'), description: t('oridd'), image: '/images/oriddust.jpg' },
    { id: 3, name: t('rg'), description: t('rgd'), image: '/images/rg.jpg' },
    { id: 4, name: t('moong'), description: t('moongd'), image: '/images/moongdust.jpg' },
    { id: 5, name: t('masoor'), description: t('masoord'), image: '/images/masoor.jpg' },
    { id: 6, name: t('maize'), description: t('maized'), image: '/images/maize1.jpg' },
    { id: 7, name: t('ricebran'), description: t('ricebd'), image: '/images/ricebran.jpg' },
    { id: 8, name: t('broad'), description: t('broadd'), image: '/images/avaraip.jpg' },
    { id: 9, name: t('sc'), description: t('scd'), image: '/images/seadcake.jpg' },
    { id: 10, name: t('pellete'), description: t('pelleted'), image: '/images/pellete.jpg' },
    { id: 11, name: t('ricef'), description: t('ricefbd'), image: '/images/ricebranflour.jpg' },
    { id: 12, name: t('pmaize'), description: t('pmaized'), image: '/images/pmaize.jpg' },
  ];

  const handleQuantityChange = (productId, quantity, category) => {
    setSelectedItems(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [productId]: quantity
      }
    }));
  };

  const clearSelection = () => {
    setSelectedItems({
      'cattle-feed': {},
      'raw-materials': {}
    });
  };

  const getTotalItems = () => {
    const cattleFeedItems = Object.values(selectedItems['cattle-feed']).reduce((sum, qty) => sum + qty, 0);
    const rawMaterialItems = Object.values(selectedItems['raw-materials']).reduce((sum, qty) => sum + qty, 0);
    return cattleFeedItems + rawMaterialItems;
  };

  const getSelectedProductsList = () => {
    const allProducts = [...cattleFeedProducts, ...rawMaterials];
    const selectedList = [];
    
    Object.entries(selectedItems['cattle-feed']).forEach(([id, qty]) => {
      if (qty > 0) {
        const product = cattleFeedProducts.find(p => p.id === parseInt(id));
        if (product) selectedList.push({ ...product, quantity: qty, category: 'cattle-feed' });
      }
    });
    
    Object.entries(selectedItems['raw-materials']).forEach(([id, qty]) => {
      if (qty > 0) {
        const product = rawMaterials.find(p => p.id === parseInt(id));
        if (product) selectedList.push({ ...product, quantity: qty, category: 'raw-materials' });
      }
    });
    
    return selectedList;
  };

  const handleInputChange = (e) => {
    setOrderForm({
      ...orderForm,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmitOrder = (e) => {
    e.preventDefault();
    
    const selectedProducts = getSelectedProductsList();
    if (selectedProducts.length === 0) {
      alert('Please select at least one product before placing an order.');
      return;
    }

    const orderDetails = selectedProducts.map(product => 
      `${product.name} - ${product.quantity} ${t('bags')}`
    ).join('\n');

    const totalBags = selectedProducts.reduce((sum, product) => sum + product.quantity, 0);
    
    const message = `New Order from ${orderForm.name}:\n\nSelected Products:\n${orderDetails}\n\nTotal Bags: ${totalBags}\nPhone: ${orderForm.phone}\nEmail: ${orderForm.email}`;
    
    const whatsappUrl = `https://wa.me/919345285338?text=${encodeURIComponent(message)}`;
    
    window.open(whatsappUrl, '_blank');
    
    // Clear selection after successful order
    clearSelection();
    setOrderForm({ name: '', phone: '', email: '' });
  };

  const renderProductCard = (product, category) => {
    const currentQuantity = selectedItems[category][product.id] || 0;

    return (
      <div key={product.id} className="product-card">
        <div className="product-image" style={{ height: `${product.height}px` }}>
          <img src={product.image} loading='lazy' alt={product.name} />
        </div>
        <div className="product-info">
          <h3>{product.name}</h3>
          <p>{product.description}</p>
          <div className="quantity-selector">
            <button 
              className="quantity-btn"
              onClick={() => handleQuantityChange(product.id, Math.max(0, currentQuantity - 1), category)}
            >
              -
            </button>
            <input
                type="number"
                className="quantity-input"
                min="0"
                max="1000"
                value={currentQuantity}
                onChange={(e) => {
                  const value = Math.max(0, Math.min(1000, Number(e.target.value)));
                  handleQuantityChange(product.id, value, category);
                }}
              />
            <button 
              className="quantity-btn"
              onClick={() => handleQuantityChange(product.id, currentQuantity + 1, category)}
            >
              +
            </button>
            <span className="quantity-label">{t('bags')}</span>
          </div>
        </div>
      </div>
    );
  };

  const selectedProducts = getSelectedProductsList();

  return (
    <div className="products">
      <section className="products-hero hero-section">
        <div className="container">
          <h1>{t('productsHero')}</h1>
          <p>{t('productsDesc')}</p>
        </div>
      </section>

      <section className="products-tabs-section">
        <div className="container">
          <div className="tabs-header">
            <button 
              className={`tab-button ${activeTab === 'cattle-feed' ? 'active' : ''}`}
              onClick={() => setActiveTab('cattle-feed')}
            >
              <i className="fas fa-cow"></i>
              {t('cattleFeed')}
            </button>
            <button 
              className={`tab-button ${activeTab === 'raw-materials' ? 'active' : ''}`}
              onClick={() => setActiveTab('raw-materials')}
            >
              <i className="fas fa-seedling"></i>
              {t('rawMaterials')}
            </button>
          </div>

          {/* Selection Summary */}
          {getTotalItems() > 0 && (
            <div className="selection-summary">
              <div className="summary-header">
                <h3>{t('selectedItems')}</h3>
                <span className="total-badge">{getTotalItems()} {t('bags')}</span>
                <button className="clear-btn" onClick={clearSelection}>
                  {t('clearSelection')}
                </button>
              </div>
              <div className="selected-items-list">
                {selectedProducts.map(product => (
                  <div key={`${product.category}-${product.id}`} className="selected-item">
                    <span>{product.name}</span>
                    <span>{product.quantity} {t('bags')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Cattle Feed Tab */}
          {activeTab === 'cattle-feed' && (
            <div className="tab-content">
              <div className="section-title">
                <h2>{t('cattleFeed')}</h2>
                <p>{t('nutritionallyBalanced')}</p>
              </div>
              
              <div className="products-grid">
                {cattleFeedProducts.map(product => renderProductCard(product, 'cattle-feed'))}
              </div>
            </div>
          )}

          {/* Raw Materials Tab */}
          {activeTab === 'raw-materials' && (
            <div className="tab-content">
              <div className="section-title">
                <h2>{t('rawMaterials')}</h2>
                <p>{t('highQualityIngredients')}</p>
              </div>
              
              <div className="products-grid">
                {rawMaterials.map(material => renderProductCard(material, 'raw-materials'))}
              </div>
            </div>
          )}

          {/* Order Form - Always visible when items are selected */}
          {getTotalItems() > 0 && (
            <div id="order-form" className="order-form-section">
              <div className="order-form">
                <h3>{t('placeOrder')}</h3>
                <p>{t('orderDesc')}</p>
                
                <form onSubmit={handleSubmitOrder}>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">{t('fullName')} *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={orderForm.name}
                        onChange={handleInputChange}
                        required
                        className="form-control"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="phone">{t('whatsappNumber')} *</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={orderForm.phone}
                        onChange={handleInputChange}
                        required
                        className="form-control"
                      />
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={orderForm.email}
                      onChange={handleInputChange}
                      required
                      className="form-control"
                    />
                  </div>

                  <div className="order-summary">
                    <h4>{t('orderSummary')}</h4>
                    <div className="summary-items">
                      {selectedProducts.map(product => (
                        <div key={`${product.category}-${product.id}`} className="summary-item">
                          <span>{product.name}</span>
                          <span>{product.quantity} {t('bags')}</span>
                        </div>
                      ))}
                    </div>
                    <div className="summary-total">
                      <strong>{t('totalItems')}: {getTotalItems()} {t('bags')}</strong>
                    </div>
                  </div>
                  
                  <button type="submit" className="btn btn-large">
                    <i className="fab fa-whatsapp"></i> {t('submitOrder')}
                  </button>
                </form>
              </div>
              
              <div className="order-info">
                <h4>{t('orderProcess')}</h4>
                <div className="process-steps">
                  <div className="step">
                    <div className="step-number">1</div>
                    <p>{t('processStep1')}</p>
                  </div>
                  <div className="step">
                    <div className="step-number">2</div>
                    <p>{t('processStep2')}</p>
                  </div>
                  <div className="step">
                    <div className="step-number">3</div>
                    <p>{t('processStep3')}</p>
                  </div>
                  <div className="step">
                    <div className="step-number">4</div>
                    <p>{t('processStep4')}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Products;